import { useState } from 'react';
import { isSystemAdmin } from '../../../utils/permissions';
import PropTypes from 'prop-types';
import DeleteIcon from '../../../assets/icons/delete.svg?react';
import ChevronDownIcon from '../../../assets/icons/chevron-down.svg?react';
import { FEATURE_NAMES, USER_ROLES } from '../../../utils/constants';
import './UserTable.css';

const UserTable = ({ 
  users, 
  isLoading, 
  isError, 
  roleFeatureMap,
  onEditUser, 
  onDeleteUser 
}) => {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (userId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const getUserFeatures = (userRoles) => {
    if (!userRoles || userRoles.length === 0) return [];
    
    const allFeatures = new Set();
    userRoles.forEach(role => {
      const features = roleFeatureMap[role] || [];
      features.forEach(feature => allFeatures.add(feature));
    });
    
    return Array.from(allFeatures);
  };

  if (isLoading) {
    return (
      <div className="primary-table">
        <table>
          <thead>
            <tr>
              <th style={{ width: '50px' }}></th>
              <th>번호</th>
              <th>이메일</th>
              <th>역할</th>
              <th>작업</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                <span className="text-b1-400 text-gray-500">로딩 중...</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="primary-table">
        <table>
          <thead>
            <tr>
              <th style={{ width: '50px' }}></th>
              <th>번호</th>
              <th>이메일</th>
              <th>역할</th>
              <th>작업</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                <span className="text-b1-400 text-error">데이터를 불러오는데 실패했습니다.</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="primary-table">
        <table>
          <thead>
            <tr>
              <th style={{ width: '50px' }}></th>
              <th>번호</th>
              <th>이메일</th>
              <th>역할</th>
              <th>작업</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan="5" style={{ textAlign: 'center', padding: '40px' }}>
                <span className="text-b1-400 text-gray-500">등록된 사용자가 없습니다.</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="primary-table user-table">
      <table>
        <thead>
          <tr>
            <th style={{ width: '50px' }}></th>
            <th>이메일</th>
            <th>역할</th>
            <th width="150px"></th>
          </tr>
        </thead>
        <tbody>
          {users.map((user, idx) => {
            const isExpanded = expandedRows.has(user.id);
            const userFeatures = getUserFeatures(user.roles);
            const hasFeatures = userFeatures.length > 0;
            if (user.roles.includes(USER_ROLES.SYSTEM_ADMIN)) {
              return null;
            }
            return (
              <>
                <tr key={user.id} className={isExpanded ? 'expanded' : ''}>
                  <td>
                    <button
                      className={`expand-btn ${isExpanded ? 'expanded' : ''} ${!hasFeatures ? 'disabled' : ''}`}
                      onClick={() => hasFeatures && toggleRow(user.id)}
                      disabled={!hasFeatures}
                      title={hasFeatures ? '피처 목록 보기' : '권한 없음'}
                    >
                      <ChevronDownIcon style={{ transform: isExpanded ? 'rotate(-180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }} />
                    </button>
                  </td>
                  <td className="user-email">
                    <p className="text-b2-400 text-gray-600">{user.email}</p>
                  </td>
                  <td>
                    <div className="flex gap-5 flex-wrap">
                      {user.roles?.length > 0 ? (
                        user.roles.map((role, roleIdx) => (
                          <span key={roleIdx} className="badge badge-primary">{role}</span>
                        ))
                      ) : (
                        <span className="badge badge-gray">역할 없음</span>
                      )}
                    </div>
                  </td>
                  <td className="button-container">
                    <div className="flex justify-center gap-5">
                      <button 
                        className="btn small-btn btn-style-5" 
                        title="수정"
                        onClick={() => onEditUser(user)}
                      >
                        역할 수정
                      </button>
                      {isSystemAdmin() && (
                      <button 
                        className="btn icon-btn"
                        title="삭제"
                        onClick={() => onDeleteUser(user)}
                      >
                        <DeleteIcon color='var(--error)' />
                      </button>
                      )}
                    </div>
                  </td>
                </tr>
                {isExpanded && hasFeatures && (
                  <tr className="expanded-row">
                    <td colSpan="5">
                      <div className="feature-details">
                        <div className="feature-details-header">
                          <span className="text-b1-600 text-gray-900">접근 가능한 피처</span>
                          <span className="badge badge-info">{userFeatures.length}개</span>
                        </div>
                        <div className="feature-list">
                          {userFeatures.map((feature, featureIdx) => (
                            <div key={featureIdx} className="feature-item">
                              <span className="feature-name">
                                {FEATURE_NAMES[feature] || feature}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

UserTable.propTypes = {
  users: PropTypes.array.isRequired,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  roleFeatureMap: PropTypes.object.isRequired,
  onEditUser: PropTypes.func.isRequired,
  onDeleteUser: PropTypes.func.isRequired,
};

UserTable.defaultProps = {
  isLoading: false,
  isError: false,
};

export default UserTable;
